// ============================================================================
// Job Scheduler Workspace Types
// ============================================================================
// Frontend domain model for the job scheduler workspace.
// Shared unions and structural types are imported from the authoritative
// electron-layer definitions via the IPC bridge (electronJobScheduler.d.ts).

import type {
  JobRunStatus, JobExecutionStatus, JobStepSubsystem, StepAction,
  JobListItem, JobStep,
  JobSchedulerCapabilities, JobSchedulerActionPermissions,
  HistoryRunStatus,
  JobScheduleFreq, JobSchedule, JobNotifications,
  JobSchedulerServiceWarning,
} from '@/types/electronJobScheduler';

// ── Re-export authoritative types for workspace consumers ────────────────
export type {
  JobRunStatus, JobExecutionStatus, JobStepSubsystem, StepAction,
  JobSchedulerCapabilities, JobSchedulerActionPermissions,
  HistoryRunStatus,
  JobScheduleFreq, JobSchedule, JobNotifications,
  JobSchedulerServiceWarning,
};

// ── Workspace-specific types ─────────────────────────────────────────────

export type JobSchedulerStatusFilter = 'all' | 'running' | 'failed' | 'succeeded' | 'disabled';

export type JobSchedulerJobItem = JobListItem;

export type JobSchedulerStepItem = JobStep;

export type JobSchedulerScheduleItem = JobSchedule;

export type JobSchedulerNotifications = JobNotifications;

export type JobSchedulerDetailData = {
  jobId: string;
  name: string;
  enabled: boolean;
  description: string | null;
  ownerLoginName: string | null;
  categoryName: string | null;
  dateCreated: string | null;
  dateModified: string | null;
  startStepId: number;
  steps: JobSchedulerStepItem[];
  schedules: JobSchedulerScheduleItem[];
  notifications: JobSchedulerNotifications;
  targetServers: string[];
};

export type HistoryStepResultItem = {
  stepId: number;
  stepName: string;
  runStatus: HistoryRunStatus;
  runDuration: string;
  message: string;
  sqlSeverity: number;
  sqlMessageId: number;
  retriesAttempted: number;
  /** Current step command from sysjobsteps (not a historical snapshot — may differ from what actually ran) */
  command?: string | null;
  /** Current step subsystem (TSQL, CmdExec, PowerShell, etc.) */
  subsystem?: string | null;
};

export type HistoryRunItem = {
  instanceId: number;
  runDate: string;
  runDuration: string;
  runStatus: HistoryRunStatus;
  message: string;
  steps: HistoryStepResultItem[];
};

export type HistoryDateRange = 'last-24h' | 'last-7d' | 'last-30d' | 'custom';

export type HistorySummaryData = {
  totalRuns: number;
  succeeded: number;
  failed: number;
  averageDurationSeconds: number;
};

export type JobSchedulerSubView = 'jobs' | 'operators' | 'alerts' | 'proxies' | 'properties' | 'error-logs';

/** Deep-link target: auto-open a specific object's editor when navigating to a subview */
export type AgentEditTarget =
  | { type: 'operator'; name: string }
  | { type: 'alert'; name: string }
  | { type: 'proxy'; name: string };

export type DetailTab = 'steps' | 'schedules' | 'history' | 'notifications' | 'general';

export type JobSchedulerTabContent = {
  connectionId?: string;
  databaseName?: string;
  /** The database engine for this tab (e.g. 'mssql', 'oracle', 'mysql', 'pgsql').
   *  Populated on first successful load from the provider.  Used to gate
   *  engine-specific UI surfaces in SubViewRouter and the editor. */
  engine?: string;
  isLoading?: boolean;
  jobs?: JobSchedulerJobItem[];
  selectedJobId?: string | null;
  subView?: JobSchedulerSubView;
  searchText?: string;
  statusFilter?: JobSchedulerStatusFilter;
  autoRefreshInterval: number;
  error?: string;
  /** Engine-neutral service warning from the provider */
  serviceWarning?: JobSchedulerServiceWarning;
  /** @deprecated Use `serviceWarning` — kept for backward compat during migration */
  agentNotRunning?: boolean;
  /** @deprecated Use `serviceWarning` — kept for backward compat during migration */
  expressEdition?: boolean;
  /** @deprecated Use `serviceWarning` — kept for backward compat during migration */
  permissionDenied?: boolean;
  capabilities?: JobSchedulerCapabilities;
  /** The login name of the current connection user (for ownership-scoped action gating) */
  currentLoginName?: string;
  jobDetail?: JobSchedulerDetailData | null;
  isLoadingDetail?: boolean;
  detailError?: string;
  /** Which detail tab to open when the detail panel loads (consumed once) */
  initialDetailTab?: DetailTab;
  historyRuns?: HistoryRunItem[];
  isLoadingHistory?: boolean;
  historyError?: string;
  historyDateRange?: HistoryDateRange;
  historyStatusFilter?: 'all' | 'failed' | 'succeeded';
  historyCustomFrom?: string;
  historyCustomTo?: string;
  historySummary?: HistorySummaryData | null;
  /** Deep-link: auto-open an editor for this object when the subview loads */
  editTarget?: AgentEditTarget;
};

/**
 * Create default content for a job scheduler tab
 */
export function createDefaultJobSchedulerContent(): JobSchedulerTabContent {
  return {
    autoRefreshInterval: 0,
    statusFilter: 'all',
    isLoading: true,
  };
}
